/**
 * OpenItems —— 應收／應付 ＋ 帳齡（P2-4；`decisions/007`）
 * ① 開單（AR 發票／AP 帳單）→ 自動出 JV 憑單 ② 收款／付款結算（出真憑單）③ 帳齡表
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import ProofAttach, { type Proof } from '../components/ProofAttach';
import { api, fmtMoney, todayISO, type AgingReport, type Meta, type OpenItem } from '../lib/api';

export default function OpenItems({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [items, setItems] = useState<OpenItem[]>([]);
  const [summary, setSummary] = useState<{ arBalance: number; apBalance: number } | null>(null);
  const [aging, setAging] = useState<AgingReport | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [settleDraft, setSettleDraft] = useState<Record<string, string>>({});
  const [settleAccount, setSettleAccount] = useState('');
  const accounts = meta.accounts.filter((a) => a.entity_id === entityId && (a.kind === 'bank' || a.kind === 'cash'));
  const [form, setForm] = useState({
    kind: 'ar', partyName: '', docNo: '', issueDate: todayISO(), dueDate: todayISO(), amount: '', glCode: '',
  });
  const [proofs, setProofs] = useState<Proof[]>([]);

  const load = useCallback(() => {
    Promise.all([
      api.openItems(entityId),
      api.aging(entityId),
    ]).then(([o, a]) => { setItems(o.items); setSummary(o.summary); setAging(a); })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const cats = form.kind === 'ar' ? meta.incomeCategories : meta.expenseCategories;

  const create = async () => {
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await api.createOpenItem({
        entityId, kind: form.kind, partyName: form.partyName, docNo: form.docNo || undefined,
        issueDate: form.issueDate, dueDate: form.dueDate, amount: form.amount,
        glCode: form.glCode || (cats[0]?.code ?? ''),
        attachmentIds: proofs.map((p) => p.id),
      });
      setOk(t('items.created', { no: r.voucherNo }));
      setForm((f) => ({ ...f, partyName: '', docNo: '', amount: '' }));
      setProofs([]);
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  const settle = async (item: OpenItem) => {
    const amount = settleDraft[item.id];
    if (!amount) { setErr(t('items.settle_amount_needed')); return; }
    const accountId = settleAccount || accounts[0]?.id || '';
    if (!accountId) { setErr(t('items.account_needed')); return; }
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await api.settleOpenItem(item.id, { amount, txnDate: todayISO(), accountId });
      setOk(t('items.settled', { no: r.voucherNo, balance: fmtMoney(r.balanceCents) }));
      setSettleDraft((s) => ({ ...s, [item.id]: '' }));
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <main className="page">
      <TopBar title={t('items.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('expense.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('items.kind')}</span>
            <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value, glCode: '' }))}>
              <option value="ar">{t('items.kind_ar')}</option>
              <option value="ap">{t('items.kind_ap')}</option>
            </select>
          </label>
          <label className="field">
            <span>{t('items.party')} *</span>
            <input value={form.partyName} onChange={(e) => setForm((f) => ({ ...f, partyName: e.target.value }))} list="customers-list" />
            <datalist id="customers-list">{meta.customers.map((c) => <option key={c.id} value={c.name} />)}</datalist>
          </label>
          <label className="field">
            <span>{t('income.doc_no')}</span>
            <input value={form.docNo} onChange={(e) => setForm((f) => ({ ...f, docNo: e.target.value }))} />
          </label>
          <label className="field">
            <span>{t('items.issue_date')} *</span>
            <input type="date" value={form.issueDate} onChange={(e) => setForm((f) => ({ ...f, issueDate: e.target.value }))} />
          </label>
          <label className="field">
            <span>{t('items.due_date')} *</span>
            <input type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
          </label>
          <label className="field">
            <span>{t('items.amount')} *</span>
            <input inputMode="decimal" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0.00" />
          </label>
          <label className="field">
            <span>{form.kind === 'ar' ? t('items.income_code') : t('items.expense_code')} *</span>
            <select value={form.glCode} onChange={(e) => setForm((f) => ({ ...f, glCode: e.target.value }))}>
              <option value="">{cats[0]?.name_zh ?? '—'}</option>
              {cats.map((c) => <option key={c.code} value={c.code}>{c.name_zh}</option>)}
            </select>
          </label>
        </div>
        <ProofAttach proofs={proofs} onChange={setProofs} disabled={busy} />
        {err && <p className="err">{err}</p>}
        {ok && <p className="ok">{ok}</p>}
        <button className="cta" onClick={() => void create()} disabled={busy || !form.partyName || !form.amount}>
          {form.kind === 'ar' ? t('items.create_ar') : t('items.create_ap')}
        </button>
        <p className="note">{t('items.note')}</p>
      </section>

      {/* 未結清清單（可部分收款／付款）*/}
      <section className="card">
        <h2>{t('items.open_list')}</h2>
        {summary && (
          <p className="note">
            {t('items.kind_ar')}：<b className="pos">{fmtMoney(summary.arBalance)}</b>　
            {t('items.kind_ap')}：<b className="neg">{fmtMoney(summary.apBalance)}</b>
          </p>
        )}
        {accounts.length > 0 && (
          <label className="field">
            <span>{t('items.settle_account')}</span>
            <select value={settleAccount || accounts[0]?.id} onChange={(e) => setSettleAccount(e.target.value)}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}
        {items.length === 0 && <p className="note">{t('list.empty')}</p>}
        <ul className="list">
          {items.map((i) => (
            <li key={i.id}>
              <span className={`tag ${i.kind === 'ar' ? 'pos' : 'neg'}`}>{i.kind === 'ar' ? 'AR' : 'AP'}</span>
              <Link className="purpose" to={`/vouchers/${i.voucher_id}`}>{i.party_name}{i.doc_no ? ` · ${i.doc_no}` : ''}</Link>
              <span>{t('items.due', { date: i.due_date })}</span>
              <span><b>{fmtMoney(i.balance_cents)}</b>{i.settled_cents > 0 ? ` / ${fmtMoney(i.amount_cents)}` : ''}</span>
              <input
                className="budget-input" inputMode="decimal" placeholder={t('items.settle_amount')}
                value={settleDraft[i.id] ?? ''} onChange={(e) => setSettleDraft((s) => ({ ...s, [i.id]: e.target.value }))}
              />
              <button onClick={() => void settle(i)} disabled={busy}>
                {i.kind === 'ar' ? t('items.receive') : t('items.pay')}
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* 帳齡 */}
      <section className="card">
        <h2>{t('items.aging')}</h2>
        {aging && (
          <>
            <table className="ledger">
              <thead>
                <tr>
                  <th>{t('items.party')}</th><th className="num">{t('items.current')}</th>
                  <th className="num">1–30</th><th className="num">31–60</th><th className="num">61–90</th>
                  <th className="num">&gt;90</th><th className="num">{t('items.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {aging.rows.map((r) => (
                  <tr key={`${r.kind}-${r.party}`}>
                    <td><span className={`tag ${r.kind === 'ar' ? 'pos' : 'neg'}`}>{r.kind === 'ar' ? 'AR' : 'AP'}</span> {r.party}</td>
                    <td className="num">{fmtMoney(r.buckets.current)}</td>
                    <td className="num">{fmtMoney(r.buckets.d1_30)}</td>
                    <td className="num">{fmtMoney(r.buckets.d31_60)}</td>
                    <td className="num">{fmtMoney(r.buckets.d61_90)}</td>
                    <td className="num">{fmtMoney(r.buckets.d90p)}</td>
                    <td className="num"><b>{fmtMoney(r.balance)}</b></td>
                  </tr>
                ))}
                <tr>
                  <td><b>{t('items.total')}</b></td>
                  <td className="num">{fmtMoney(aging.bucketTotals.ar.current + aging.bucketTotals.ap.current)}</td>
                  <td className="num">{fmtMoney(aging.bucketTotals.ar.d1_30 + aging.bucketTotals.ap.d1_30)}</td>
                  <td className="num">{fmtMoney(aging.bucketTotals.ar.d31_60 + aging.bucketTotals.ap.d31_60)}</td>
                  <td className="num">{fmtMoney(aging.bucketTotals.ar.d61_90 + aging.bucketTotals.ap.d61_90)}</td>
                  <td className="num">{fmtMoney(aging.bucketTotals.ar.d90p + aging.bucketTotals.ap.d90p)}</td>
                  <td className="num"><b>{fmtMoney(aging.totals.ar + aging.totals.ap)}</b></td>
                </tr>
              </tbody>
            </table>
            <p className="note">{aging.note}｜asOf：{aging.asOf}</p>
          </>
        )}
      </section>
    </main>
  );
}
